import { FileText } from "lucide-react";
import React from "react";

import { HoverTooltip } from "@/common/components/tooltip/HoverTooltip";

interface RequirementTooltipProps {
  req: any;
  children: React.ReactNode;
}

/**
 * Requirement hover card — content-only wrapper around the shared
 * {@link HoverTooltip} so requirement badges show the same dark surface,
 * arrow and typography as every other entity tooltip.
 */
export function RequirementTooltip({ req, children }: RequirementTooltipProps) {
  const reqId = `REQ-${String(req.id).padStart(4, "0")}`;
  const title = req.name || req.title || "Untitled requirement";
  const type = req.requirementtype || req.type || "";

  const description = (() => {
    const raw = req.description ?? req.summary ?? "";
    if (!raw) return "";
    return String(raw)
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();
  })();

  return (
    <HoverTooltip
      width={260}
      delayMs={400}
      estimatedHeight={170}
      align="start"
      content={
        <>
          {/* Title */}
          <div className="space-y-0.5">
            <p className="text-[10px] font-medium text-popover-foreground/60 font-mono">
              {reqId}
            </p>
            <p className="text-[12px] font-semibold leading-tight text-popover-foreground">
              {title}
            </p>
          </div>

          {/* Description */}
          {description && (
            <p className="text-[11px] leading-snug text-popover-foreground/70 line-clamp-3">
              {description}
            </p>
          )}

          {/* Type */}
          {type && (
            <div className="flex items-center gap-1.5 text-[11px] text-popover-foreground/70">
              <FileText className="h-3 w-3" />
              <span>{type}</span>
            </div>
          )}

          {/* Hint */}
          <p className="text-[10px] text-info pt-0.5">
            Click ID to open requirement ↗
          </p>
        </>
      }
    >
      {children}
    </HoverTooltip>
  );
}
